import { useCallback } from 'react';
import { pettyCashService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { PettyCash } from '../types';

export function usePettyCash(
  options?: ResourceQueryOptions
): ResourceState<PettyCash> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      pettyCashService.getAll(opts) as unknown as Promise<{ data: PettyCash[]; meta?: any }>,
    []
  );
  return useResourceCollection<PettyCash>(fetchFn, options);
}

export function usePettyCashById(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<PettyCash> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (pettyCashId: number, opts?: ResourceQueryOptions) =>
      pettyCashService.getById(pettyCashId, opts) as unknown as Promise<PettyCash>,
    []
  );
  return useResourceById<PettyCash>(fetchFn, numericId, options);
}

export function useCreatePettyCash(
  onSuccess?: (data: PettyCash) => void
): MutationState<PettyCash> {
  const createFn = useCallback(
    (data: any) => pettyCashService.create(data) as unknown as Promise<PettyCash>,
    []
  );
  return useCreateResource<PettyCash>(createFn, onSuccess);
}

export function useUpdatePettyCash(
  onSuccess?: (data: PettyCash) => void
): MutationState<PettyCash> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return pettyCashService.update(numericId, data) as unknown as Promise<PettyCash>;
    },
    []
  );
  return useUpdateResource<PettyCash>(updateFn, onSuccess);
}

export function useDeletePettyCash(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return pettyCashService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

